import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import '../styles/header.css';

const Header = ({ isScrolled: externalIsScrolled }) => {
  const [internalIsScrolled, setInternalIsScrolled] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use external isScrolled prop if provided, otherwise use internal scroll detection
  const isScrolled = externalIsScrolled !== undefined ? externalIsScrolled : internalIsScrolled;

  useEffect(() => {
    // Only use internal scroll detection if no external prop is provided
    if (externalIsScrolled === undefined) {
      const handleScroll = () => {
        const scrollTop = window.scrollY;
        // Only apply dark background on desktop (> 768px) or after scrolling past hero on mobile
        const isMobile = window.innerWidth <= 768;
        const threshold = isMobile ? 100 : 600; // Mobile header darkens after 100px scroll
        setInternalIsScrolled(scrollTop > threshold);
      };

      handleScroll(); // Run once on mount
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [externalIsScrolled]);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsDropdownOpen(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <header className={`header-new ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-new-content">
        {/* Mobile Burger Menu */}
        <button 
          className="header-mobile-burger"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className="header-new-left">
          <nav className="header-new-nav">
            <Link to="/events" className="header-new-nav-item header-new-nav-link">
              {t('nav.events', language)}
            </Link>
            <Link to="/private-events" className="header-new-nav-item header-new-nav-link">
              {t('nav.privateEvents', language)}
            </Link>
                  <Link to="/the-venue" className="header-new-nav-item header-new-nav-link">
                    {t('nav.venue', language)}
                  </Link>
                  <Link to="/contact" className="header-new-nav-item header-new-nav-link">
                    {t('nav.contact', language)}
                  </Link>
          </nav>
        </div>
        <div className="header-new-center">
          <Link to="/" className="header-new-logo-link">
            <img 
              src="/assets/PawilonTextLogo.svg" 
              alt="Pawilon Krzyżanowskiego" 
              className="header-new-logo"
            />
          </Link>
        </div>
        <div className="header-new-right">
          <Link to="/events" className="header-new-tickets-button">
            {language === 'pl' ? 'Kup Bilety' : 'Buy Tickets'}
          </Link>
        </div>
      </div>

      {/* Mobile Slide Menu */}
      <div className={`header-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <nav className="header-mobile-nav">
          <Link to="/events" className="header-mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            {t('nav.events', language)}
          </Link>
          <Link to="/private-events" className="header-mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            {t('nav.privateEvents', language)}
          </Link>
          <Link to="/the-venue" className="header-mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            {t('nav.venue', language)}
          </Link>
          <Link to="/contact" className="header-mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            {t('nav.contact', language)}
          </Link>
        </nav>
        <Link to="/events" className="header-mobile-button" onClick={() => setIsMobileMenuOpen(false)}>
          {language === 'pl' ? 'Kup Bilety' : 'Buy Tickets'}
        </Link>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="header-mobile-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Fixed Language Switcher */}
      <div 
        className={`header-new-lang-switcher ${isDropdownOpen ? 'expanded' : ''}`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        {!isDropdownOpen ? (
          <div className="header-new-lang-closed">
            <span className="header-new-lang-current">{language}</span>
          </div>
        ) : (
          <div className="header-new-lang-radio-container">
            <span className="header-new-lang-current">{language}</span>
            <span className="header-new-lang-separator">|</span>
            <label className="header-new-lang-radio">
              <input
                type="radio"
                name="language"
                value="pl"
                checked={language === 'pl'}
                onChange={() => handleLanguageChange('pl')}
              />
              <span onClick={() => handleLanguageChange('pl')}>pl</span>
            </label>
            <label className="header-new-lang-radio">
              <input
                type="radio"
                name="language"
                value="en"
                checked={language === 'en'}
                onChange={() => handleLanguageChange('en')}
              />
              <span onClick={() => handleLanguageChange('en')}>en</span>
            </label>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
